<template>
  <div class="w-full">
    <div
      class="flex gap-6 border-b border-steel-300 bg-white px-3 pb-4 md:border-none"
    >
      <span
        class="cursor-pointer"
        :class="{
          'border-b-2 border-sun-700 text-sun-700': selectedTab === 'Active',
          'text-base text-steel-700': selectedTab !== 'Active',
          'flex-1': !isDesktop,
        }"
        @click="emit('handle-tab-selection', 'Active')"
      >
        Active
      </span>
      <span
        class="cursor-pointer"
        :class="{
          'border-b-2 border-sun-700 text-sun-700': isExpired,
          'text-base text-steel-700': !isExpired,
          'flex-1': !isDesktop,
        }"
        @click="emit('handle-tab-selection', 'Expired')"
      >
        Expired
      </span>
    </div>

    <div class="px-3">
      <div v-if="isDesktop">
        <div
          class="grid grid-cols-16 rounded-t-lg border border-steel-300 bg-steel-100 px-2 py-3"
        >
          <div class="col-span-3 text-start text-sm text-steel-1100">
            Broker
          </div>
          <div class="col-span-3 text-start text-sm text-steel-1100">
            Load ID
          </div>
          <div class="col-span-3 text-start text-sm text-steel-1100">
            Origin
          </div>
          <div class="col-span-3 text-start text-sm text-steel-1100">
            Destination
          </div>
          <div class="col-span-3 text-start text-sm text-steel-1100">
            Expiration
          </div>
          <div class="col-span-1 text-start text-sm text-steel-1100">
            Amount
          </div>
        </div>

        <div
          v-if="is_offers_loading"
          class="grid min-h-[500px] items-center border border-steel-300 px-2"
        >
          <img
            class="loading m-auto my-8 h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>

        <div
          v-else-if="offers?.length > 0"
          class="max-h-[calc(100vh-330px)] overflow-scroll"
        >
          <div
            v-for="offer in offers"
            :key="offer.id"
            class="grid grid-cols-16 items-center border border-steel-300 px-2"
            :class="{
              'cursor-pointer hover:bg-sun-100': !isExpired,
            }"
            @click="handleOfferSelection(offer)"
          >
            <div class="col-span-3 flex flex-col items-start gap-1 py-3">
              <img
                class="flex h-8 items-center text-2xs"
                :src="offer?.load?.broker_logo?.url"
                alt="broker logo"
              />
            </div>

            <div class="col-span-3 flex flex-col items-start gap-1 py-3">
              <p
                class="whitespace-normal break-all pr-3 text-left text-sm font-normal text-steel-800"
              >
                {{ offer?.load?.external_order_or_load_number }}
              </p>
            </div>

            <div class="col-span-3 flex flex-col items-start gap-1 py-3">
              <p
                v-if="offer?.load?.stops?.length > 0"
                class="text-left text-sm font-normal text-steel-800"
              >
                {{ offer?.load?.stops?.[0]?.city
                }}{{
                  offer?.load?.stops?.[0]?.state
                    ? `, ${offer?.load?.stops?.[0]?.state}`
                    : ''
                }}
              </p>
              <p
                v-else
                class="text-left text-sm font-normal text-steel-800"
              >
                -
              </p>
              <p class="text-xs font-light text-steel-800">
                {{
                  formatStopDate(
                    offer?.load?.pickup_time,
                    offer?.load?.pickup_time_timezone,
                    offer?.load?.pickup_has_time
                  )
                }}
              </p>
            </div>

            <div class="col-span-3 flex flex-col items-start gap-1 py-3">
              <p
                v-if="offer?.load?.stops?.length > 0"
                class="text-left text-sm font-normal text-steel-800"
              >
                {{ offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.city
                }}{{
                  offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.state
                    ? `, ${offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.state}`
                    : ''
                }}
              </p>
              <p
                v-else
                class="text-left text-sm font-normal text-steel-800"
              >
                -
              </p>
              <p class="text-xs font-light text-steel-800">
                {{
                  formatStopDate(
                    offer?.load?.dropoff_time,
                    offer?.load?.dropoff_time_timezone,
                    offer?.load?.dropoff_has_time
                  )
                }}
              </p>
            </div>

            <div class="col-span-3 flex flex-col items-start gap-1 py-3">
              <div
                v-if="offer.expires_at"
                class="text-left"
              >
                <p class="text-sm font-normal text-steel-800">
                  {{ untilFirstUnit(offer.expires_at) }}
                </p>
                <p class="text-xs font-light text-steel-800">
                  {{
                    formatStopDate(
                      offer?.expires_at,
                      offer?.load?.dropoff_time_timezone,
                      true
                    )
                  }}
                </p>
              </div>
              <p
                v-else
                class="text-sm font-normal text-steel-800"
              >
                -
              </p>
            </div>

            <div class="col-span-1 flex flex-col items-end gap-1 py-3">
              <p class="text-xl font-semibold">
                {{ formatPrice(offer.rate, 0) }}
              </p>
              <p
                v-if="formatPrice(offer?.load?.rate_per_mile) && offer.rate"
                class="text-xs font-normal text-steel-700"
              >
                {{ formatPrice(offer?.load?.rate_per_mile) }}/mi
              </p>
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex min-h-[500px] flex-col items-center justify-center gap-2 border border-steel-300"
        >
          <img
            class=""
            src="@/assets/offers-icon.svg"
            alt="offers"
          />
          <p class="text-lg text-steel-700">
            {{
              isExpired
                ? "There aren't any expired offers"
                : "There aren't any active offers"
            }}
          </p>
        </div>

        <div
          v-if="offers?.length > 0"
          class="relative flex min-h-[60px] items-center justify-center rounded-b-lg border border-steel-300 px-2 py-3"
        >
          <p class="absolute left-2 text-xs font-medium text-steel-900">
            Showing {{ offers?.length }} of
            {{ offers_pagination?.totalRecords }} results
          </p>

          <pagination
            v-if="offers?.length && offers_pagination?.totalPages > 1"
            :selected-page="offers_pagination?.currentPage"
            :total-pages="offers_pagination?.totalPages"
            :alignment="'center'"
            @select-page="onSelectPage"
          />
        </div>
      </div>

      <!-- mobile view -->
      <div
        v-else
        class="pt-4"
      >
        <div
          v-if="is_offers_loading"
          class="min-h-[500px] items-center px-2"
        >
          <img
            class="loading m-auto my-8 h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>
        <div
          v-else-if="offers?.length > 0"
          class="mb-8 flex flex-wrap justify-center gap-4"
        >
          <div class="flex-between w-full">
            <p class="w-full text-left text-sm font-medium text-steel-900">
              Showing {{ offers?.length }} of
              {{ offers_pagination?.totalRecords }}
              Offer{{ offers?.length === 1 ? '' : 's' }}
            </p>

            <ButtonComponent
              variant="subtle"
              class="flex gap-1 pl-3 pr-6"
              @click="toggleSortOfferBottomSheet"
            >
              <img
                class=""
                src="@/assets/arrows-up-and-down.svg"
                alt="loading"
              />
              Sort</ButtonComponent
            >
          </div>

          <div
            v-for="offer in offers"
            :key="offer.id"
            class="flex w-full max-w-[400px] flex-col gap-1 rounded-lg border border-steel-200 border-steel-300 bg-white px-3 py-1 text-left shadow-md"
          >
            <div class="flex-between">
              <img
                class="flex h-8 items-center text-2xs"
                :src="offer?.load?.broker_logo?.url"
                alt="broker logo"
              />

              <div class="flex flex-col items-end gap-1 py-3">
                <p class="text-2xl font-bold">
                  {{ formatPrice(offer.rate, 0) }}
                </p>

                <p
                  v-if="formatPrice(offer?.load?.rate_per_mile) && offer.rate"
                  class="text-xs font-normal text-steel-700"
                >
                  {{ formatPrice(offer?.load?.rate_per_mile) }}/mi
                </p>
              </div>
            </div>

            <div class="flex-between">
              <p class="flex-1 text-sm text-steel-700">Load ID</p>

              <p class="flex-1 text-right text-sm font-normal text-steel-900">
                {{ offer?.load?.external_order_or_load_number }}
              </p>
            </div>

            <div class="flex-between">
              <p class="text-sm text-steel-700">Expiration</p>

              <div
                v-if="offer.expires_at"
                class="flex flex-col items-end gap-1 py-3"
              >
                <p class="text-sm font-normal text-steel-900">
                  {{ untilFirstUnit(offer.expires_at) }}
                </p>
                <p class="text-xs font-light text-steel-900">
                  {{
                    formatStopDate(
                      offer?.expires_at,
                      offer?.load?.dropoff_time_timezone,
                      true
                    )
                  }}
                </p>
              </div>
              <p
                v-else
                class="text-sm font-normal text-steel-800"
              >
                -
              </p>
            </div>

            <div class="flex-between">
              <p class="text-sm text-steel-700">Origin</p>

              <div class="flex flex-col items-end gap-1 py-3">
                <p
                  v-if="offer?.load?.stops?.length > 0"
                  class="text-left text-sm font-normal text-steel-900"
                >
                  {{ offer?.load?.stops?.[0]?.city
                  }}{{
                    offer?.load?.stops?.[0]?.state
                      ? `, ${offer?.load?.stops?.[0]?.state}`
                      : ''
                  }}
                </p>
                <p
                  v-else
                  class="text-left text-sm font-normal text-steel-900"
                >
                  -
                </p>
                <p class="text-sm font-light text-steel-900">
                  {{
                    formatStopDate(
                      offer?.load?.pickup_time,
                      offer?.load?.pickup_time_timezone,
                      offer?.load?.pickup_has_time
                    )
                  }}
                </p>
              </div>
            </div>

            <div class="flex-between">
              <p class="text-sm text-steel-700">Destination</p>

              <div class="flex flex-col items-end gap-1 py-3">
                <p
                  v-if="offer?.load?.stops?.length > 0"
                  class="text-left text-sm font-normal text-steel-900"
                >
                  {{ offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.city
                  }}{{
                    offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.state
                      ? `, ${offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.state}`
                      : ''
                  }}
                </p>
                <p
                  v-else
                  class="text-left text-sm font-normal text-steel-900"
                >
                  -
                </p>
                <p class="text-sm font-light text-steel-900">
                  {{
                    formatStopDate(
                      offer?.load?.dropoff_time,
                      offer?.load?.dropoff_time_timezone,
                      offer?.load?.dropoff_has_time
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <InfiniteScroll @load-more="loadMoreOffers"></InfiniteScroll>

        <div
          v-if="isOffersRefetching && !is_offers_loading"
          class="pb-12 pt-6"
        >
          <img
            class="loading m-auto h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading spinner"
          />
        </div>
        <div
          v-else-if="offers?.length === 0 && !is_offers_loading"
          class="mt-4 flex flex-col items-center gap-3"
        >
          <img
            class=""
            src="@/assets/offers-icon.svg"
            alt="offers"
          />
          <p class="text-lg text-steel-700">
            {{
              isExpired
                ? "There aren't any expired offers"
                : "There aren't any active offers"
            }}
          </p>
        </div>
      </div>
    </div>

    <PlaceOfferBottomSheet
      type="edit"
      :visible="showPlaceOfferBottomSheet"
      :offer="selectedOffer"
      @close="closePlaceOfferBottomSheet"
    >
    </PlaceOfferBottomSheet>
    <OffersSortBottomSheet
      :visible="showSortOfferBottomSheet"
      @close="toggleSortOfferBottomSheet"
    >
    </OffersSortBottomSheet>
  </div>
</template>

<script setup>
import { computed, ref, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { ButtonComponent, Pagination, InfiniteScroll } from '@/components';
import {
  OffersSortBottomSheet,
  PlaceOfferBottomSheet,
} from '@/components/loads';
import { formatStopDate } from '@/utils/loadBoardUtils.js';
import { untilFirstUnit } from '@/common/timestamps';
import { formatPrice } from '@/utils/currency.js';

const store = useStore();

const props = defineProps({
  isDesktop: {
    type: Boolean,
    default: false,
  },
  selectedTab: {
    type: String,
  },
});

const emit = defineEmits(['handle-tab-selection']);

const isExpired = computed(() => props?.selectedTab === 'Expired');

const active_offers = computed(() => store.state.loads?.active_offers);
const active_offers_pagination = computed(
  () => store.state.loads?.active_offers_pagination
);

const expired_offers = computed(() => store.state.loads?.expired_offers);
const expired_offers_pagination = computed(
  () => store.state.loads?.expired_offers_pagination
);

const offers = computed(() =>
  isExpired?.value ? expired_offers?.value : active_offers?.value
);

const offers_pagination = computed(() =>
  isExpired.value
    ? expired_offers_pagination?.value
    : active_offers_pagination?.value
);

const is_offers_loading = computed(() => store.state.loads?.is_offers_loading);

const isOffersRefetching = ref(false);

const loadMoreOffers = async () => {
  console.log('Hit infinite scroll');

  if (isExpired?.value) {
    if (
      expired_offers_pagination?.value?.totalRecords > offers?.value?.length
    ) {
      isOffersRefetching.value = true;

      // fetch next page expired offers
      await store.dispatch('loads/getExpiredOffers', {
        page: expired_offers_pagination?.value?.currentPage + 1,
        refetch: true,
      });
    }
    isOffersRefetching.value = false;
  } else {
    if (active_offers_pagination?.value?.totalRecords > offers?.value?.length) {
      isOffersRefetching.value = true;

      // fetch next page active offers
      await store.dispatch('loads/getOffers', {
        page: active_offers_pagination?.value?.currentPage + 1,
        refetch: true,
      });

      isOffersRefetching.value = false;
    }
  }
};

const onSelectPage = (page) => {
  console.log('select page', page);
  if (isExpired?.value) {
    store.dispatch('loads/getExpiredOffers', {
      page,
    });
  } else {
    store.dispatch('loads/getOffers', {
      page,
    });
  }
};

const selectedOffer = ref({});
const showPlaceOfferBottomSheet = ref(false);
const closePlaceOfferBottomSheet = () => {
  selectedOffer.value = {};
  showPlaceOfferBottomSheet.value = false;
};

const showSortOfferBottomSheet = ref(false);
const toggleSortOfferBottomSheet = () => {
  showSortOfferBottomSheet.value = !showSortOfferBottomSheet.value;
};

const handleOfferSelection = async (offer) => {
  console.log('offer selected: ', offer);
  // commented out for now -- 8/26/25
  // if (!isExpired?.value) {
  //   selectedOffer.value = offer;
  //   showPlaceOfferBottomSheet.value = true;
  // }
};
</script>
